import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';

interface ContentSectionProps {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  backgroundColor?: string;
  py?: number;
}

export const ContentSection: React.FC<ContentSectionProps> = ({
  title,
  subtitle,
  children,
  maxWidth = 'lg',
  backgroundColor,
  py = 4,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        py,
        backgroundColor: backgroundColor || 'transparent',
        width: '100%',
      }}
    >
      <Box sx={{ maxWidth: maxWidth ? theme.breakpoints.values[maxWidth] : 'none', mx: 'auto', px: 2 }}>
        {(title || subtitle) && (
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            {title && (
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                component="h2"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.primary.main,
                  mb: subtitle ? 2 : 0,
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant={isMobile ? 'body1' : 'h6'}
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                  maxWidth: '800px',
                  mx: 'auto',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
};

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}

export const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
  action,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            color: theme.palette.primary.main,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontWeight: 'bold',
            mb: 2,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            mb: action ? 2 : 0,
          }}
        >
          {description}
        </Typography>
        {action && <Box>{action}</Box>}
      </CardContent>
    </Card>
  );
};

interface StatsDisplayProps {
  stats: Array<{
    value: string;
    label: string;
    description?: string;
  }>;
}

export const StatsDisplay: React.FC<StatsDisplayProps> = ({ stats }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Grid container spacing={3}>
      {stats.map((stat, index) => (
        <Grid item xs={6} md={3} key={index}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                mb: 1,
              }}
            >
              {stat.value}
            </Typography>
            <Typography
              variant={isMobile ? 'body2' : 'body1'}
              sx={{
                fontWeight: 'bold',
                color: theme.palette.text.primary,
                mb: stat.description ? 1 : 0,
              }}
            >
              {stat.label}
            </Typography>
            {stat.description && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  display: 'block',
                }}
              >
                {stat.description}
              </Typography>
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};
